/**
 * 効果音の音程設計（純粋関数のみ。WebAudio に依存しないためテストしやすい）。
 *
 * 合体音は「レベルが上がるほど高くなる」よう、日本の五音音階（陽音階）へ
 * 割り当てる。半音階で単純に上げるより和風でまとまりが出て、
 * 連鎖したときに旋律として気持ちよく聞こえる。
 */

/** 陽音階（ヨナ抜き長音階）: ド・レ・ミ・ソ・ラ。 */
export const YO_SCALE_SEMITONES = [0, 2, 4, 7, 9] as const;

/** 陰音階（都節）: 和風で少し陰のある響き。ゲームオーバー等に使う。 */
export const IN_SCALE_SEMITONES = [0, 1, 5, 7, 8] as const;

/** 基準となる音（C4）。 */
export const ROOT_HZ = 261.63;

/** 音階の n 番目（オクターブをまたいで無限に続く）の半音数を返す。 */
export function scaleSemitone(scale: readonly number[], step: number): number {
  const size = scale.length;
  const octave = Math.floor(step / size);
  const index = ((step % size) + size) % size;
  return scale[index] + octave * 12;
}

/** 半音数から周波数へ。 */
export function semitoneToHz(semitone: number, rootHz: number = ROOT_HZ): number {
  return rootHz * Math.pow(2, semitone / 12);
}

/**
 * 合体音の基音。
 * レベル2（最初の合体結果）を音階の 0 番目とし、レベル12 まで上がっていく。
 */
export function mergeNoteHz(level: number): number {
  const step = Math.max(0, Math.round(level) - 2);
  return semitoneToHz(scaleSemitone(YO_SCALE_SEMITONES, step));
}

/**
 * コンボ時に重ねる上ハモリ。
 * 連鎖が続くほど上の音を重ねて高揚感を出す。
 */
export function comboNoteHz(level: number, combo: number): number {
  const base = Math.max(0, Math.round(level) - 2);
  const lift = Math.min(6, Math.max(0, Math.round(combo) - 1) + 2);
  return semitoneToHz(scaleSemitone(YO_SCALE_SEMITONES, base + lift));
}

/**
 * 投下音の基音。
 * 落とす料理が大きいほど低く鳴らして「重さ」を伝える。
 */
export function dropNoteHz(level: number): number {
  const clamped = Math.min(12, Math.max(1, Math.round(level)));
  return semitoneToHz(-24 + (12 - clamped) * 0.8);
}
